import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Minus,
  Plus,
  Calendar as CalendarIcon,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import {
  fetchPublicTreatmentRetreatPackageById,
  fetchApplicableCommission,
  type PublicTreatmentRetreatPackage,
} from "./treatmentRetreatPublicApi";

const toArray = <T,>(value: T[] | null | undefined): T[] =>
  Array.isArray(value) ? value : [];

const formatCurrency = (amount?: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

interface DateOption {
  dateString: string; // YYYY-MM-DD
  dayLabel: string;   // TODAY, TMRW, SAT, SUN...
  dayNum: string;     // 29, 30...
  monthLabel: string; // Oct, Nov...
}

const generateUpcomingDates = (count = 15): DateOption[] => {
  const dates: DateOption[] = [];
  const today = new Date();

  for (let i = 0; i < count; i++) {
    const d = new Date();
    d.setDate(today.getDate() + i);

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const dateString = `${year}-${month}-${day}`;

    let dayLabel = "";
    if (i === 0) dayLabel = "TODAY";
    else if (i === 1) dayLabel = "TMRW";
    else {
      dayLabel = d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
    }

    const dayNum = String(d.getDate());
    const monthLabel = d.toLocaleDateString("en-US", { month: "short" });

    dates.push({ dateString, dayLabel, dayNum, monthLabel });
  }
  return dates;
};

interface PricingOptionItem {
  type: string;
  price: number;
  platformFee?: number;
}

export const WellnessBookPackagePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { packageId } = useParams<{ packageId: string }>();
  const dateInputRef = useRef<HTMLInputElement | null>(null);

  const statePackageData = (location.state as any)?.packageData as PublicTreatmentRetreatPackage | undefined;
  const [packageData, setPackageData] = useState<PublicTreatmentRetreatPackage | null>(
    statePackageData || null
  );
  const [loading, setLoading] = useState<boolean>(!statePackageData);
  const [commissionPercentage, setCommissionPercentage] = useState<number | null>(
    (location.state as any)?.commissionPercentage ?? null
  );
  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>(
    (location.state as any)?.selectedQuantities || {}
  );

  const upcomingDates = useMemo(() => generateUpcomingDates(15), []);

  // Default selected date to Tomorrow (or location.state)
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    return (location.state as any)?.bookingDate || upcomingDates[1]?.dateString || upcomingDates[0]?.dateString;
  });

  const [showCalendarView, setShowCalendarView] = useState<boolean>(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  // Inline month calendar calculations matching Image 3
  const calendarData = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstDayIndex = new Date(year, month, 1).getDay(); // 0 is Sun, 1 is Mon...
    const startingBlankDays = (firstDayIndex + 6) % 7; // Mon = 0
    const totalDaysInMonth = new Date(year, month + 1, 0).getDate();

    const days: Array<{
      dayNum: number;
      dateString: string;
      isPast: boolean;
      isSelected: boolean;
    }> = [];

    for (let day = 1; day <= totalDaysInMonth; day++) {
      const dateObj = new Date(year, month, day);
      dateObj.setHours(0, 0, 0, 0);
      const isPast = dateObj < today;
      const dateString = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const isSelected = dateString === selectedDate;
      days.push({ dayNum: day, dateString, isPast, isSelected });
    }

    return {
      monthLabel: calendarMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
      startingBlankDays,
      days,
      canGoPrev: !(year <= today.getFullYear() && month <= today.getMonth()),
    };
  }, [calendarMonth, selectedDate]);

  const handlePrevMonth = () => {
    if (!calendarData.canGoPrev) return;
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // Initialize or fetch package data
  useEffect(() => {
    let isMounted = true;

    const loadPackage = async () => {
      const targetId = packageId || statePackageData?._id;
      if (!targetId) {
        setLoading(false);
        return;
      }

      if (!packageData) {
        try {
          const res = await fetchPublicTreatmentRetreatPackageById(targetId);
          if (res?.success && res.data && isMounted) {
            setPackageData(res.data);
            initQuantities(res.data);

            try {
              const comm = await fetchApplicableCommission({
                serviceId: res.data._id,
                vendorId: (res.data as any).mentorId,
              });
              if (isMounted) setCommissionPercentage(comm);
            } catch (err) { }
          }
        } catch (e) {
          console.error("Error fetching package for booking:", e);
        } finally {
          if (isMounted) setLoading(false);
        }
      } else {
        if (Object.keys(selectedQuantities).length === 0) {
          initQuantities(packageData);
        }
        if (commissionPercentage === null) {
          fetchApplicableCommission({
            serviceId: packageData._id,
            vendorId: (packageData as any).mentorId,
          })
            .then((comm) => {
              if (isMounted) setCommissionPercentage(comm);
            })
            .catch(() => { });
        }
      }
    };

    const initQuantities = (pkg: PublicTreatmentRetreatPackage) => {
      const initial: Record<string, number> = {};
      const options = toArray(pkg.pricingOptions);
      if (options.length > 0) {
        options.forEach((opt) => {
          initial[opt.type] = 0;
        });
        initial[options[0].type] = 1;
      } else {
        initial["Standard"] = 1;
      }
      setSelectedQuantities(initial);
    };

    loadPackage();

    return () => {
      isMounted = false;
    };
  }, [packageId, statePackageData]);

  const pricingOptions: PricingOptionItem[] = useMemo(() => {
    if (!packageData) return [];
    const opts = toArray(packageData.pricingOptions);
    if (opts.length > 0) return opts;
    return [{ type: "Per Person", price: packageData.price || 0 }];
  }, [packageData]);

  const updateQuantity = (type: string, delta: number) => {
    setSelectedQuantities((prev) => {
      const current = prev[type] ?? 0;
      const next = Math.max(0, current + delta);
      return {
        ...prev,
        [type]: next,
      };
    });
  };

  const getBaseTotal = (): number => {
    return pricingOptions.reduce((sum, opt) => {
      const qty = selectedQuantities[opt.type] ?? 0;
      return sum + opt.price * qty;
    }, 0);
  };

  const totalParticipants = useMemo(() => {
    return Object.values(selectedQuantities).reduce((sum, q) => sum + (q || 0), 0);
  }, [selectedQuantities]);

  const getCommissionRate = (): number => {
    if (typeof commissionPercentage === "number") return commissionPercentage;
    if (typeof packageData?.commissionPercentage === "number") return packageData.commissionPercentage;
    return 0;
  };

  const baseTotal = useMemo(() => getBaseTotal(), [packageData, selectedQuantities, pricingOptions]);
  const commissionRate = useMemo(() => getCommissionRate(), [commissionPercentage, packageData]);
  const commissionFee = useMemo(() => {
    if (commissionRate > 0 && baseTotal > 0) {
      return Math.round(((baseTotal * commissionRate) / 100) * 100) / 100;
    }
    return 0;
  }, [baseTotal, commissionRate]);
  const estimatedTotal = useMemo(() => Math.round((baseTotal + commissionFee) * 100) / 100, [baseTotal, commissionFee]);

  const handleContinueToPreview = () => {
    if (!packageData || baseTotal <= 0) return;
    if (!selectedDate) {
      alert("Please select a date for your retreat.");
      return;
    }

    const payload = {
      packageData,
      selectedQuantities,
      baseAmount: baseTotal,
      commissionPercentage: commissionRate,
      commissionAmount: commissionFee,
      totalAmount: estimatedTotal,
      bookingDate: selectedDate,
    };

    try {
      sessionStorage.setItem("current_booking_preview", JSON.stringify(payload));
    } catch (e) { }

    navigate("/luxury-wellness-retreats/booking-preview", {
      state: payload,
    });
  };

  const isCustomDateSelected = useMemo(() => {
    return !upcomingDates.some((d) => d.dateString === selectedDate);
  }, [upcomingDates, selectedDate]);

  const customDateFormatted = useMemo(() => {
    if (!selectedDate) return "";
    try {
      const d = new Date(selectedDate);
      return {
        dayLabel: d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
        dayNum: String(d.getDate()),
        monthLabel: d.toLocaleDateString("en-US", { month: "short" }),
      };
    } catch (e) {
      return null;
    }
  }, [selectedDate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-[#15302B]/20 border-t-[#15302B] rounded-full animate-spin" />
          <p className="text-xs font-semibold text-[#15302B]">Loading retreat details...</p>
        </div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
        <div className="max-w-md w-full rounded-2xl bg-white border border-slate-200 p-8 text-center shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Package Not Found</h2>
          <p className="text-xs text-slate-500 mt-1.5">
            The requested retreat package could not be found.
          </p>
          <Link
            to="/luxury-wellness-retreats"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#E05638] px-4 py-2 text-xs font-bold text-white hover:bg-[#C54528] transition-colors"
          >
            Back to Packages
          </Link>
        </div>
      </div>
    );
  }

  const cleanTitle = packageData.packageName?.replace(/^\d+[\s._-]+/i, "").trim() || packageData.packageName;

  const offerPrice = Number(packageData.price || 0);
  const rawOldPrice = Number(packageData.oldPrice || 0);
  const hasDiscount = rawOldPrice > offerPrice && offerPrice > 0;
  const oldPrice = hasDiscount ? rawOldPrice : null;
  const youSave = hasDiscount ? rawOldPrice - offerPrice : 0;
  const discountPercent = hasDiscount ? Math.round((youSave / rawOldPrice) * 100) : 0;

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#FAF9F6] text-[#1E293B] antialiased py-3 sm:py-5 px-3 sm:px-6 lg:px-8 pb-20 sm:pb-24">
      <div className="max-w-4xl mx-auto space-y-2.5 sm:space-y-3">
        {/* Top Back Navigation Arrow */}
        <div className="pt-0.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center text-[#E05638] hover:text-[#C54528] transition-colors cursor-pointer"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5 stroke-[2.5]" />
          </button>
        </div>

        {/* 1. Package Summary Header Card with Price like Image 2 */}
        <div className="rounded-xl border border-slate-200/80 bg-white px-3.5 py-2.5 sm:px-4 sm:py-3 shadow-2xs flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base font-bold text-[#1E293B] leading-tight">
              {cleanTitle}
            </h1>
            <p className="text-[11px] sm:text-xs text-[#64748B] mt-0.5 flex flex-wrap items-center gap-1.5">
              <span>
                {packageData.duration ? `${packageData.duration} stay` : "Daily"}
              </span>
              <span>•</span>
              <span>
                {packageData.startTime && packageData.endTime
                  ? `${packageData.startTime} to ${packageData.endTime} (IST)`
                  : "08:00 AM to 10:00 PM (IST)"}
              </span>
              {[packageData.city, packageData.state].filter(Boolean).length > 0 && (
                <>
                  <span>•</span>
                  <span>{[packageData.city, packageData.state].filter(Boolean).join(", ")}</span>
                </>
              )}
            </p>
          </div>

          {/* Price display matching Image 2 */}
          <div className="flex flex-col sm:items-end shrink-0 pt-1.5 sm:pt-0 border-t sm:border-t-0 border-slate-100">
            <span className="text-[9px] sm:text-[10px] font-semibold text-slate-500 uppercase tracking-wider leading-none mb-0.5">
              Starting from
            </span>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-sm sm:text-base font-bold text-[#E05638] leading-tight">
                {formatCurrency(offerPrice)}
              </span>
              <span className="text-[10px] sm:text-[11px] text-slate-500 font-medium">
                / {packageData.bookingType?.toLowerCase().includes("slot") ? "slot" : "person"}
              </span>
              {hasDiscount && (
                <span className="text-[10px] sm:text-[11px] line-through text-slate-400">
                  {formatCurrency(oldPrice!)}
                </span>
              )}
              {hasDiscount && (
                <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full border border-emerald-200">
                  {discountPercent}% OFF
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 2. Select Date Section */}
        <div className="rounded-xl border border-slate-200/80 bg-white px-3.5 py-2.5 sm:px-4 sm:py-3 shadow-2xs space-y-2.5">
          {/* Section Header with Orange Accent Bar */}
          <div className="flex items-center gap-1.5">
            <span className="w-1 h-3.5 bg-[#E05638] rounded-full inline-block" />
            <h2 className="font-bold text-xs sm:text-[13px] text-[#1E293B]">
              Select Date
            </h2>
          </div>

          {!showCalendarView ? (
            /* Quick Dates View (Exactly 2 rows on mobile: 4 cols x 2 rows = 8 tiles; 2 rows on desktop: 8 cols x 2 rows = 16 tiles) */
            <div className="grid grid-cols-4 md:grid-cols-8 gap-1.5 sm:gap-2 pt-0.5">
              {upcomingDates.map((item, index) => {
                const isSelected = selectedDate === item.dateString;
                // On mobile (< md), hide items beyond the first 7 so it stays strictly 2 rows of 4 (7 dates + 1 "See all")
                const isHiddenOnMobile = index >= 7;

                return (
                  <button
                    key={item.dateString}
                    type="button"
                    onClick={() => setSelectedDate(item.dateString)}
                    className={`${isHiddenOnMobile ? "hidden md:flex" : "flex"} flex-col items-center justify-center rounded-lg border py-1.5 px-1 sm:px-2 w-full transition-all cursor-pointer select-none ${
                      isSelected
                        ? "bg-[#0F172A] text-white border-[#0F172A] shadow-xs"
                        : "bg-white text-[#334155] border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <span
                      className={`text-[9px] sm:text-[10px] font-semibold tracking-wider ${
                        isSelected ? "text-slate-300" : "text-[#64748B]"
                      }`}
                    >
                      {item.dayLabel}
                    </span>
                    <span className="text-sm sm:text-base font-bold my-0.5 leading-none">
                      {item.dayNum}
                    </span>
                    <span
                      className={`text-[9px] sm:text-[10px] font-medium ${
                        isSelected ? "text-slate-300" : "text-[#64748B]"
                      }`}
                    >
                      {item.monthLabel}
                    </span>
                  </button>
                );
              })}

              {/* See All Dates Tile -> toggles Image 3 calendar view */}
              <button
                type="button"
                onClick={() => {
                  if (selectedDate) {
                    const d = new Date(selectedDate);
                    if (!isNaN(d.getTime())) setCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                  }
                  setShowCalendarView(true);
                }}
                className="flex flex-col items-center justify-center rounded-lg border border-slate-200 bg-white py-1.5 px-1 sm:px-2 w-full hover:border-slate-300 hover:bg-slate-50 transition-all cursor-pointer text-[#64748B]"
              >
                <CalendarIcon className="h-3.5 w-3.5 mb-0.5 text-[#64748B]" />
                <span className="text-[9px] font-semibold leading-tight text-center">
                  See all
                  <br />
                  dates
                </span>
              </button>
            </div>
          ) : (
            /* Image 3: Full Inline Month Calendar View */
            <div className="space-y-3 pt-1">
              {/* Month Navigation Header */}
              <div className="flex items-center justify-between px-2 sm:px-4 py-1">
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  disabled={!calendarData.canGoPrev}
                  className="p-1 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-20 disabled:hover:bg-transparent cursor-pointer transition-colors"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-xs sm:text-sm font-semibold text-slate-800">
                  {calendarData.monthLabel}
                </span>
                <button
                  type="button"
                  onClick={handleNextMonth}
                  className="p-1 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 cursor-pointer transition-colors"
                  aria-label="Next month"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Weekday Names Header (Mon - Sun matching Image 3) */}
              <div className="grid grid-cols-7 gap-1 text-center">
                {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((day) => (
                  <span
                    key={day}
                    className="text-[10px] sm:text-xs font-semibold text-slate-400 tracking-wider py-1"
                  >
                    {day}
                  </span>
                ))}
              </div>

              {/* Days Grid */}
              <div className="grid grid-cols-7 gap-1 text-center">
                {/* Leading Blank Cells */}
                {Array.from({ length: calendarData.startingBlankDays }).map((_, i) => (
                  <div key={`blank-${i}`} className="py-2" />
                ))}

                {/* Actual Days */}
                {calendarData.days.map((d) => (
                  <button
                    key={d.dateString}
                    type="button"
                    disabled={d.isPast}
                    onClick={() => setSelectedDate(d.dateString)}
                    className={`py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg transition-all select-none ${
                      d.isSelected
                        ? "bg-[#0F172A] text-white font-bold shadow-xs cursor-pointer"
                        : d.isPast
                        ? "text-slate-300 cursor-not-allowed"
                        : "text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-medium cursor-pointer"
                    }`}
                  >
                    {d.dayNum}
                  </button>
                ))}
              </div>

              {/* Back to Quick Dates button (Matching Image 3) */}
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowCalendarView(false)}
                  className="rounded-lg border border-slate-200/90 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors shadow-2xs cursor-pointer"
                >
                  Back to quick dates
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 3. Pricing / Participant Options Card */}
        <div className="space-y-2">
          {pricingOptions.map((option) => {
            const quantity = selectedQuantities[option.type] ?? 0;
            return (
              <div
                key={option.type}
                className="flex items-center justify-between rounded-xl border border-slate-200/80 bg-white px-3.5 py-2 sm:px-4 sm:py-2.5 shadow-2xs"
              >
                <div>
                  <h3 className="text-[11px] sm:text-xs font-bold tracking-wider text-[#1E293B] uppercase">
                    {option.type}
                  </h3>
                  <div className="flex items-baseline gap-1.5 flex-wrap mt-0.5">
                    <span className="text-xs sm:text-sm font-bold text-[#E05638]">
                      {formatCurrency(option.price)}
                    </span>
                    <span className="text-[10px] text-slate-500 font-medium">
                      / {packageData.bookingType?.toLowerCase().includes("slot") ? "slot" : "person"}
                    </span>
                    {hasDiscount && option.price === offerPrice && (
                      <>
                        <span className="text-[10px] line-through text-slate-400">
                          {formatCurrency(oldPrice!)}
                        </span>
                        <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1 py-0.2 rounded border border-emerald-200">
                          {discountPercent}% OFF
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Soft pill counter */}
                <div className="flex items-center gap-2.5 bg-[#F1F5F9] border border-slate-200/80 rounded-lg px-2 py-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => updateQuantity(option.type, -1)}
                    disabled={quantity <= 0}
                    className="flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center rounded text-[#334155] hover:text-black font-bold cursor-pointer disabled:opacity-25 transition-all"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="min-w-[14px] text-center font-bold text-xs sm:text-sm text-[#0F172A] select-none">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => updateQuantity(option.type, 1)}
                    className="flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center rounded text-[#2563EB] hover:text-blue-800 font-bold cursor-pointer transition-all"
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 4. Fixed Bottom Total & Next Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/90 py-2 sm:py-2.5 px-4 sm:px-8 shadow-[0_-8px_30px_rgba(0,0,0,0.08)]">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 sm:gap-4">
          <div>
            <p className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider leading-none mb-0.5">
              {totalParticipants} PERSON{totalParticipants !== 1 ? "S" : ""}
            </p>
            <p className="text-base sm:text-lg font-bold text-[#0F172A] leading-tight">
              {formatCurrency(estimatedTotal)}
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              disabled={baseTotal <= 0 || !selectedDate}
              onClick={handleContinueToPreview}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0F172A] hover:bg-neutral-800 text-white px-5 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-bold shadow-xs transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <span>Next</span>
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessBookPackagePage;
